import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MainNavigation from './MainNavigation';
import OrganizationSelector from './OrganizationSelector';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useUserStore } from '../../store';
import { useTimeclockStore } from '../../storeTimeclock';
import TopBarClockWidget from '../worktime/clock/TopBarClockWidget';
import ClockWatchers from '../worktime/clock/ClockWatchers';
import BillingAlertBanner from '../shared/BillingAlertBanner';
import CommandPaletteHost from '../shared/CommandPalette/CommandPaletteHost';
import { MobileSearchTrigger, SidebarSearchTrigger } from '../shared/CommandPalette/SearchTrigger';
import { MobileSupportButton, SidebarSupportButton } from '../support/SupportNavButton';
import useCalendarState from '../calendar/hooks/useCalendarState';

const DRAWER_WIDTH = 260;

// Pages that lay out their own viewport and should use the whole window width.
// The 1400px reading cap below suits text-heavy pages, but the calendar gets one
// column per mechanic, so on a wide monitor the cap is what makes it cramped.
const FULL_WIDTH_PATHS = ['/calendar'];

// Logo section component (reused in sidebar and mobile drawer)
function LogoSection() {
  return (
    <Box
      sx={{
        p: 3,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Box
        component={Link}
        to="/"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src="/img/logo-smaller.png"
            alt="Verstaani"
            sx={{
              height: 32,
              width: 'auto',
            }}
          />
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '22px',
            fontWeight: 600,
            color: 'white',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            letterSpacing: '-0.5px',
          }}
        >
          Verstaani
        </Box>
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const isFullWidthPage = FULL_WIDTH_PATHS.includes(pathname.replace(/\/+$/, '') || '/');
  // Opt-in, per browser, and only ever honoured on a full-width page, so leaving the calendar
  // always brings the navigation back. Toggled from the calendar header.
  const navHidden = useCalendarState((s) => s.navHidden);
  const navCollapsed = !isMobile && isFullWidthPage && navHidden;
  const contentOffset = navCollapsed ? 0 : DRAWER_WIDTH;
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileNavigate = () => {
    setMobileOpen(false);
  };

  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const startPolling = useTimeclockStore((s) => s.startPolling);
  const stopPolling = useTimeclockStore((s) => s.stopPolling);
  const orgId = currentOrganization?.organization_id;
  // Live clock surfaces (pill, watchers, /active+/day poll) belong to the opt-in timer system.
  const timeClockEnabled = currentOrganization?.time_clock_enabled ?? false;
  // employee_id === null → confirmed unlinked (e.g. org admin with no Employee row): no clock
  // surfaces, no poll. undefined (older /users/me payload) deliberately counts as linked.
  const noEmployee = currentOrganization?.employee_id === null;
  const clockEnabled = timeClockEnabled && !noEmployee;

  // App-wide poll of /active+/day (drives the top-bar pill and watchers). Only when the org has
  // the live time-clock enabled and the user has a linked employee; restarts on org switch.
  useEffect(() => {
    if (orgId && clockEnabled) {
      startPolling();
    }
    return () => stopPolling();
  }, [orgId, clockEnabled, startPolling, stopPolling]);

  // Sidebar content (shared between desktop sidebar and mobile drawer). The search trigger and
  // the clock pill are desktop-only here (mobile shows both in the AppBar).
  const sidebarContent = (
    <>
      <LogoSection />
      <OrganizationSelector />
      {!isMobile && <SidebarSearchTrigger />}
      {!isMobile && clockEnabled && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <TopBarClockWidget />
        </Box>
      )}
      {/* First row of the menu: support must be found before the phone number is */}
      <SidebarSupportButton onNavigate={isMobile ? handleMobileNavigate : undefined} />
      <MainNavigation onNavigate={isMobile ? handleMobileNavigate : undefined} />
    </>
  );

  return (
    <CommandPaletteHost>
      <Box sx={{ display: 'flex', minHeight: '100vh', overflowX: 'hidden', maxWidth: '100vw' }}>
        {/* Mobile AppBar */}
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{
              bgcolor: 'primary.main',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              {/* Mobile Logo */}
              <Box
                component={Link}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src="/img/logo-smaller.png"
                    alt="Verstaani"
                    sx={{
                      height: 24,
                      width: 'auto',
                    }}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'white',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    letterSpacing: '-0.5px',
                    // Search, support and the clock pill do not fit next to the wordmark on a
                    // narrow phone; the logo alone says Verstaani
                    ...(clockEnabled && { '@media (max-width: 440px)': { display: 'none' } }),
                  }}
                >
                  Verstaani
                </Box>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <MobileSearchTrigger />
              <MobileSupportButton />
              {clockEnabled && <TopBarClockWidget compact />}
            </Toolbar>
          </AppBar>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                bgcolor: 'primary.main',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {sidebarContent}
          </Drawer>
        )}

        {/* Desktop Sidebar */}
        {!isMobile && !navCollapsed && (
          <Box
            component="nav"
            data-testid="app-nav-drawer"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              position: 'fixed',
              left: 0,
              top: 0,
              height: '100vh',
              bgcolor: 'primary.main',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1200,
              overflow: 'auto',
            }}
          >
            {sidebarContent}
          </Box>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            ml: isMobile ? 0 : `${contentOffset}px`,
            mt: isMobile ? '64px' : 0, // AppBar height offset for mobile
            minHeight: isMobile ? 'calc(100vh - 64px)' : '100vh',
            bgcolor: 'background.default',
            overflow: 'hidden',
            width: isMobile ? '100%' : `calc(100% - ${contentOffset}px)`,
          }}
        >
          <Box sx={{
            // Vertical padding stays put: MainCalendarView sizes itself against it.
            py: { xs: 2, md: 4 },
            px: isFullWidthPage ? { xs: 1, md: 2 } : { xs: 2, md: 4 },
            maxWidth: isFullWidthPage ? 'none' : '1400px',
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}>
            {/* Unpaid subscription / overdue invoice / trial ending. Above every page on
                purpose: a customer who owes us money should not have to find this. */}
            <BillingAlertBanner />
            <Outlet />
          </Box>
        </Box>
        {clockEnabled && <ClockWatchers />}
      </Box>
    </CommandPaletteHost>
  );
}
